import type { TodayFreshness } from '@/types';

/**
 * How far today is ahead of (+) or behind (-) yesterday at the same time of
 * day, as a whole percent. null when yesterday has nothing to compare with.
 */
export function pacePercent(today: number, yesterdaySameTime: number): number | null {
  if (!Number.isFinite(today) || !Number.isFinite(yesterdaySameTime) || yesterdaySameTime <= 0) {
    return null;
  }
  return Math.round(((today - yesterdaySameTime) / yesterdaySameTime) * 100);
}

/**
 * Why a tile's pace is hidden: today's or yesterday's numbers only start at
 * the given HH:MM (IST), so comparing them would show a false ▲/▼. null when
 * both days are fully counted and the pace can be shown.
 */
export function paceHiddenReason(
  todaySince: string | null,
  yesterdaySince: string | null
): string | null {
  const parts: string[] = [];
  if (todaySince) parts.push(`Today counted since ${todaySince}`);
  if (yesterdaySince) parts.push(`Yesterday counted since ${yesterdaySince}`);
  return parts.length ? parts.join(' · ') : null;
}

/** Time left as m:ss, whole seconds rounded up; 0:00 once due (or late). */
export function countdownText(msLeft: number): string {
  const s = Number.isFinite(msLeft) && msLeft > 0 ? Math.ceil(msLeft / 1000) : 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const inrFormat = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

/** Rupees with Indian digit grouping and no decimals: 4687000 -> ₹46,87,000. */
export function formatInr(n: number): string {
  return `₹${inrFormat.format(Number.isFinite(n) ? n : 0)}`;
}

/** A count with Indian digit grouping: 212377 -> 2,12,377. */
export function formatCount(n: number | null | undefined): string {
  return n === null || n === undefined || !Number.isFinite(n) ? '—' : inrFormat.format(n);
}

const FRESHNESS_COLORS: Record<TodayFreshness, string> = {
  ok: '#52c41a',
  stale: '#faad14',
  dead: '#ff4d4f',
};

/** Dot colour of a freshness state: ok green, stale amber, dead red. */
export function freshnessColor(state: TodayFreshness): string {
  return FRESHNESS_COLORS[state] ?? FRESHNESS_COLORS.dead;
}

const IST_OFFSET_MS = 5.5 * 3_600_000;

const istIso = (iso: string): string | null => {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms + IST_OFFSET_MS).toISOString() : null;
};

/**
 * HH:MM on the IST wall clock of an instant. Computed from the offset, not the
 * browser's time zone: the admin may be viewing this from outside India.
 */
export function istClock(iso: string): string {
  return istIso(iso)?.slice(11, 16) ?? '';
}

/** HH:MM:SS on the IST wall clock of an instant. */
export function istClockSeconds(iso: string): string {
  return istIso(iso)?.slice(11, 19) ?? '';
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Sun 18 Oct" for the response's IST day (YYYY-MM-DD). */
export function istDayLabel(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}
